import { Field, FieldType, FieldVisibility } from "./Field";
import { ProductObject } from "./Product";

// Named label constants used in both PRECHECK_REQUIRED_FIELDS and PRECHECK_FIELD_LABELS
// to avoid fragile array-index references and fragile string literals scattered across packages.
const FULL_LEGAL_NAME_LABEL = "Full Legal Name";
const DATE_OF_BIRTH_LABEL = "Date of birth";
const ZIP_CODE_LABEL = "ZIP or Postal Code";
const PHONE_LABEL = "Phone";
const PAYMENT_AUTH_LABEL = "Payment Authorization";
const PHOTO_ID_FRONT_LABEL = "Photo ID Front";
const PHOTO_ID_BACK_LABEL = "Photo ID Back";
const SELFIE_LABEL = "Selfie";
const SIGNATURE_LABEL = "Signature";

/**
 * Product selection for precheck.
 * Includes both the product type and ID to support multiple product types.
 */
export interface PreCheckProductSelection {
    id: ID;
    object: ProductObject;
}

/**
 * Selection state for precheck products.
 * Can select all products or specific products by type and ID.
 */
export interface PreCheckProductsSelection {
    all: boolean;
    selected: PreCheckProductSelection[];
}

/**
 * Required fields for precheck forms.
 * These fields must be present in every experience form when precheck is enabled.
 */
export const PRECHECK_REQUIRED_FIELDS: Omit<Field, "id">[] = [
    {
        label: FULL_LEGAL_NAME_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.TEXT,
        readonly: false,
        seq: 10,
        visibility: FieldVisibility.ALL,
        description: "Your full legal name as it appears on your government-issued ID",
    },
    {
        label: DATE_OF_BIRTH_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.DATE_OF_BIRTH,
        readonly: true,
        seq: 20,
        visibility: FieldVisibility.ALL,
        age: 0,
        comparator: "older",
        verifyAge: true,
    },
    {
        label: ZIP_CODE_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.TEXT,
        readonly: false,
        seq: 30,
        visibility: FieldVisibility.ALL,
        description: "Your ZIP or Postal Code",
    },
    {
        label: PHONE_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.PHONE,
        readonly: true,
        seq: 40,
        visibility: FieldVisibility.ALL,
    },
    {
        label: PAYMENT_AUTH_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.CHECKBOX,
        readonly: false,
        seq: 50,
        visibility: FieldVisibility.ALL,
        description: "Purchaser has authorized payment for this purchase",
    },
    {
        label: PHOTO_ID_FRONT_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.PHOTO,
        readonly: false,
        seq: 60,
        visibility: FieldVisibility.ADULTS,
        description: "Front of Payer's Photo ID",
    },
    {
        label: PHOTO_ID_BACK_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.PHOTO,
        readonly: false,
        seq: 70,
        visibility: FieldVisibility.ADULTS,
        description: "Back of Payer's Photo ID",
    },
    {
        label: SELFIE_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.PHOTO,
        readonly: false,
        seq: 80,
        visibility: FieldVisibility.ADULTS,
        description: "Payer's Selfie Photo",
    },
    {
        label: SIGNATURE_LABEL,
        organizerOnly: true,
        addToSavedViews: false,
        required: false,
        roster: false,
        type: FieldType.PHOTO,
        readonly: false,
        seq: 90,
        visibility: FieldVisibility.ADULTS,
        description: "Payer's Signature",
    },
];

/**
 * Photo field labels for precheck.
 * Use these when uploading photos to ensure consistency with the form field definitions.
 */
export const PRECHECK_PHOTO_LABELS = {
    ID_FRONT: PHOTO_ID_FRONT_LABEL,
    ID_BACK: PHOTO_ID_BACK_LABEL,
    SELFIE: SELFIE_LABEL,
    SIGNATURE: SIGNATURE_LABEL,
} as const;

/**
 * All precheck field labels (text + photo).
 * Use these constants instead of string literals or array-index access into PRECHECK_REQUIRED_FIELDS.
 */
export const PRECHECK_FIELD_LABELS = {
    FULL_NAME: FULL_LEGAL_NAME_LABEL,
    DATE_OF_BIRTH: DATE_OF_BIRTH_LABEL,
    ZIP_CODE: ZIP_CODE_LABEL,
    PHONE: PHONE_LABEL,
    PAYMENT_AUTH: PAYMENT_AUTH_LABEL,
    ...PRECHECK_PHOTO_LABELS,
} as const;
